'use client';

import { useEffect } from 'react';

/**
 * Global link click logger. Attach once at the root layout level.
 * Captures all internal link clicks (same-origin <a> tags) and logs them
 * via /api/log/link-click. Fire-and-forget, non-blocking.
 *
 * Skips: external links, anchor-only links (#), API and auth routes, static assets.
 */
export function useLinkClickLog() {
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      // Skip external links, anchors, API routes, assets
      if (
        href.startsWith('http') ||
        href.startsWith('#') ||
        href.startsWith('/api/') ||
        // Login/logout: not a page view, and a log request racing
        // /auth/logout could re-set the session cookie it clears.
        href.startsWith('/auth/') ||
        href.startsWith('/_next/') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:')
      ) {
        return;
      }

      const text = anchor.textContent?.trim().slice(0, 100) || undefined;

      fetch('/api/log/link-click', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          href,
          text,
          page: window.location.pathname + window.location.search,
        }),
        keepalive: true,
      }).catch(() => {});
    }

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);
}
